/**
 * Connection Queue - Prevents connection pool exhaustion
 *
 * Serializes database requests to prevent overwhelming the connection pool.
 * Allows only a limited number of concurrent requests.
 */

type QueuedRequest<T> = {
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
};

class ConnectionQueue {
  private queue: QueuedRequest<unknown>[] = [];
  private activeRequests = 0;
  private readonly maxConcurrent: number;

  constructor(maxConcurrent: number = 3) {
    this.maxConcurrent = maxConcurrent;
  }

  async enqueue<T>(request: () => Promise<T>): Promise<T> {
    // If under limit, execute immediately
    if (this.activeRequests < this.maxConcurrent) {
      return this.executeRequest(request);
    }

    // Otherwise, add to queue
    return new Promise<T>((resolve, reject) => {
      this.queue.push({
        execute: request,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });
  }

  private async executeRequest<T>(request: () => Promise<T>): Promise<T> {
    this.activeRequests++;

    try {
      const result = await request();
      return result;
    } finally {
      this.activeRequests--;
      this.processNext();
    }
  }

  private processNext(): void {
    if (this.queue.length === 0 || this.activeRequests >= this.maxConcurrent) {
      return;
    }

    const next = this.queue.shift();
    if (!next) return;

    this.executeRequest(next.execute)
      .then(next.resolve)
      .catch(next.reject);
  }

  getStats() {
    return {
      activeRequests: this.activeRequests,
      queuedRequests: this.queue.length,
      maxConcurrent: this.maxConcurrent,
    };
  }
}

// Singleton instance - allows max 3 concurrent database requests
export const connectionQueue = new ConnectionQueue(3);
